/**
 * AuthClient — pure TypeScript class for OIDC/PKCE authentication.
 *
 * Handles the BFF (Backend-For-Frontend) auth flow:
 * 1. Check for existing session (agion_sso_session HttpOnly cookie)
 * 2. Redirect to auth-service /authorize for OIDC login with PKCE
 * 3. Handle callback — exchange authorization code for session
 * 4. Logout — clear server-side session from Redis
 *
 * No React dependency. No env var reads. Config passed via constructor.
 * Extracted from admin-ui sso-client.ts + chat-ui OIDCAuthManager.
 */

import type { AuthConfig, AuthUser, SessionCheckResult, SessionResponse } from './types'
import { keysToCamel } from './utils'

const LOG_PREFIX = '[auth]'
export const AUTH_CALLBACK_PATH = '/auth/callback'

export function isAuthCallbackPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').toLowerCase() === AUTH_CALLBACK_PATH
}

export class AuthCallbackError extends Error {
  constructor(readonly reason: 'missing_code' | 'missing_state' | 'state_mismatch' | 'missing_verifier' | 'authorization_rejected', message: string) {
    super(message)
    this.name = 'AuthCallbackError'
  }
}

/**
 * The one validator for "go back to this page" links carried through a URL,
 * session storage or a query parameter. It returns the same-site path (with
 * query and fragment) or '/' for anything else: another origin, a
 * protocol-relative or backslash address, control characters, or the sign-in
 * callback. Return destinations are app routes, never a consumed
 * authorization response.
 */
export function safeReturnPath(path: string): string {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return '/'
  for (const character of path) {
    if (character.charCodeAt(0) <= 0x20) return '/'
  }
  try {
    const url = new URL(path, window.location.origin)
    const pathname = decodeURIComponent(url.pathname)
    if (url.origin !== window.location.origin || isAuthCallbackPath(pathname)) return '/'
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/'
  }
}

export class AuthClient {
  private readonly authUrl: string
  private readonly loginUrl: string
  private readonly appName: string

  constructor(config: AuthConfig) {
    this.authUrl = config.authUrl
    this.loginUrl = config.loginUrl
    this.appName = config.appName

    console.debug(LOG_PREFIX, 'AuthClient initialized', {
      authUrl: this.authUrl,
      appName: this.appName,
    })
  }

  /**
   * Validate existing BFF session via GET /session.
   *
   * Returns `ok` with the user and the raw SessionResponse (for app-specific
   * fields like onboardingState), `unauthenticated` when the session is gone
   * (401, or 200 without a signed-in user), or `unavailable` when the
   * sign-in service could not answer (5xx or a network error), with the
   * Retry-After seconds when the server sent them.
   */
  async checkSession(): Promise<SessionCheckResult> {
    let response: Response
    try {
      response = await fetch(`${this.authUrl}/session`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      })
    } catch (error) {
      console.warn(LOG_PREFIX, 'Session check could not reach the sign-in service', error)
      return { status: 'unavailable' }
    }

    if (response.status >= 500) {
      const retryAfterSec = retryAfterSeconds(response)
      console.warn(LOG_PREFIX, 'Session check unavailable', { status: response.status, retryAfterSec })
      return retryAfterSec === undefined ? { status: 'unavailable' } : { status: 'unavailable', retryAfterSec }
    }

    if (!response.ok) {
      console.debug(LOG_PREFIX, 'No valid session', { status: response.status })
      return { status: 'unauthenticated' }
    }

    let data: SessionResponse
    try {
      data = keysToCamel(await response.json())
    } catch (error) {
      console.error(LOG_PREFIX, 'Session check returned an unreadable body', error)
      return { status: 'unauthenticated' }
    }

    if (!data.authenticated || !data.userId) {
      console.debug(LOG_PREFIX, 'Session check returned unauthenticated')
      return { status: 'unauthenticated' }
    }

    const user = sessionResponseToUser(data)
    return { status: 'ok', user, sessionData: data }
  }

  /**
   * Generate PKCE code verifier and SHA-256 challenge.
   * See: https://datatracker.ietf.org/doc/html/rfc7636
   */
  private async generatePKCE(): Promise<{ verifier: string; challenge: string }> {
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    const verifier = btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')

    const encoder = new TextEncoder()
    const hash = await crypto.subtle.digest('SHA-256', encoder.encode(verifier))
    const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')

    return { verifier, challenge }
  }

  /**
   * Redirect to auth-service /authorize for OIDC login with PKCE.
   * Stores PKCE verifier and CSRF state in sessionStorage.
   */
  async initiateSSO(returnPath?: string): Promise<void> {
    const actualReturnPath = safeReturnPath(returnPath ?? window.location.pathname + window.location.search)
    const callbackURL = `${window.location.origin}${AUTH_CALLBACK_PATH}`

    const pkce = await this.generatePKCE()
    sessionStorage.setItem('pkce_verifier', pkce.verifier)

    const state = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
    sessionStorage.setItem('oauth_state', state)
    sessionStorage.setItem('sso_return_path', actualReturnPath)

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.appName,
      redirect_uri: callbackURL,
      scope: 'openid profile email',
      state,
      code_challenge: pkce.challenge,
      code_challenge_method: 'S256',
    })

    const authorizeURL = `${this.authUrl}/authorize?${params}`
    console.debug(LOG_PREFIX, 'Initiating OIDC authorization', { appName: this.appName })
    window.location.href = authorizeURL
  }

  /**
   * Handle callback from OIDC authorization server.
   * Validates CSRF state, exchanges code for session cookie via POST /token,
   * then fetches user session.
   */
  async handleCallback(code: string, state: string): Promise<{ user: AuthUser; sessionData: SessionResponse }> {
    if (!code) {
      throw new AuthCallbackError('missing_code', 'No authorization code provided in callback')
    }

    const expectedState = sessionStorage.getItem('oauth_state')
    if (!state || state !== expectedState) {
      throw new AuthCallbackError(!state || !expectedState ? 'missing_state' : 'state_mismatch', 'Invalid state parameter')
    }
    sessionStorage.removeItem('oauth_state')

    const codeVerifier = sessionStorage.getItem('pkce_verifier')
    if (!codeVerifier) {
      throw new AuthCallbackError('missing_verifier', 'PKCE verifier not found')
    }
    sessionStorage.removeItem('pkce_verifier')

    // Exchange authorization code for session cookie
    const tokenResponse = await fetch(`${this.authUrl}/token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: this.appName,
        code,
        redirect_uri: `${window.location.origin}/auth/callback`,
        code_verifier: codeVerifier,
      }),
    })

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json().catch(() => ({ error: 'Unknown error' }))
      const errorMessage =
        typeof errorData === 'object' && errorData !== null && 'error' in errorData
          ? String(errorData.error)
          : 'Unknown error'
      throw new Error(errorMessage || `Token exchange failed: ${tokenResponse.statusText}`)
    }

    // Fetch user session after token exchange
    const sessionResponse = await fetch(`${this.authUrl}/session`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    })

    if (!sessionResponse.ok) {
      throw new Error('Failed to fetch user session after token exchange')
    }

    const sessionData: SessionResponse = keysToCamel(await sessionResponse.json())
    if (!sessionData.authenticated || !sessionData.userId) {
      throw new Error('No user data returned from session endpoint')
    }

    const user = sessionResponseToUser(sessionData)
    console.debug(LOG_PREFIX, 'OIDC authentication completed', { email: user.email })
    return { user, sessionData }
  }

  /**
   * Lightweight keepalive. GETs `/session/touch` on auth-service which
   * triggers rolling refresh + Redis SessionTTL reset on the server side.
   * No tokens or claims are returned to the browser.
   *
   * Returns true on HTTP 200, false otherwise (including 401, network error,
   * and 5xx). The caller (keepalive loop) discriminates 401 from transient
   * errors via handleUnauthorized.
   */
  async touchSession(touchUrl?: string): Promise<boolean> {
    const url = touchUrl ?? `${this.authUrl}/session/touch`
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      return response.ok
    } catch (err) {
      console.debug(LOG_PREFIX, 'touchSession network error', err)
      return false
    }
  }

  /**
   * Logout — clears BFF session from Redis, redirects to login.
   */
  async logout(): Promise<void> {
    try {
      await fetch(`${this.authUrl}/session/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }).catch((error) => {
        console.warn(LOG_PREFIX, 'Logout call failed, continuing with cleanup', error)
      })
    } finally {
      // Clean up sessionStorage auth artifacts
      sessionStorage.removeItem('pkce_verifier')
      sessionStorage.removeItem('oauth_state')
      sessionStorage.removeItem('sso_return_path')

      window.location.href = this.loginUrlReturningHere()
    }
  }

  /**
   * Login UI URL whose continuation is this app's own address, so a fresh
   * sign-in lands back here. auth-service checks the address and, for a
   * Guest, the console then forwards to the chat app (ADR-20260930 D7).
   * Login UI derives no app address from a name.
   */
  private loginUrlReturningHere(): string {
    try {
      const url = new URL(this.loginUrl)
      url.searchParams.set('return_to', `${window.location.origin}/`)
      return url.toString()
    } catch {
      return this.loginUrl
    }
  }

  /** Get and clear the stored return path from SSO flow */
  getReturnPath(): string {
    const returnPath = sessionStorage.getItem('sso_return_path') || '/'
    sessionStorage.removeItem('sso_return_path')
    return safeReturnPath(returnPath)
  }
}

/** Convert raw SessionResponse to AuthUser */
function sessionResponseToUser(data: SessionResponse): AuthUser {
  return {
    id: data.userId,
    email: data.email,
    name: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email,
    role: (data.role?.toLowerCase() || 'user') as AuthUser['role'],
    organizationId: data.organizationId,
    organizationName: data.organizationName,
    firstName: data.firstName,
    lastName: data.lastName,
    fullName: data.fullName,
    avatar: data.avatarUrl,
    avatarUrl: data.avatarUrl,
    authProvider: data.authProvider as AuthUser['authProvider'],
    platformAdmin: data.platformAdmin,
    inviterName: data.inviterName,
    guestHintDismissed: data.guestHintDismissed,
    homeApp: data.homeApp,
    homeUrl: data.homeUrl,
  }
}

/**
 * Retry-After of a response in whole seconds: delta-seconds or an HTTP date.
 * Undefined when the header is absent or unreadable.
 */
function retryAfterSeconds(response: Response): number | undefined {
  const raw = response.headers?.get('Retry-After')?.trim()
  if (!raw) return undefined
  if (/^\d+$/.test(raw)) return Number(raw)
  const at = Date.parse(raw)
  if (Number.isNaN(at)) return undefined
  return Math.max(0, Math.ceil((at - Date.now()) / 1000))
}
